import { Radio, RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export type RadioFieldOption = {
  value: string;
  title: string;
  subtitle?: string;
  icon?: IconProp;
};

type Props = {
  label?: string;
  value: string;
  options: RadioFieldOption[];
  className?: string;
  onChange: (value: string) => void;
};

export function RadioField({
  label,
  value,
  options,
  className,
  onChange,
}: Props) {
  return (
    <div className={classNames(styles.field.wrapper, className)}>
      {label && <p className={styles.field.label}>{label}</p>}

      <RadioGroup value={value} onChange={onChange} className={styles.group}>
        {options.map((opt) => (
          <Radio
            key={opt.value}
            value={opt.value}
            className={({ checked }) =>
              classNames(
                styles.card.base,
                checked ? styles.card.selected : styles.card.idle,
              )
            }
          >
            {({ checked }) => (
              <>
                {opt.icon ? (
                  <span
                    className={classNames(
                      styles.icon.wrap,
                      checked ? styles.icon.wrapSelected : styles.icon.wrapIdle,
                    )}
                    aria-hidden="true"
                  >
                    <FontAwesomeIcon
                      icon={opt.icon}
                      className={
                        checked
                          ? styles.icon.iconSelected
                          : styles.icon.iconIdle
                      }
                    />
                  </span>
                ) : null}

                <span className={styles.text.wrap}>
                  <span className={styles.text.title}>{opt.title}</span>
                  {opt.subtitle ? (
                    <span className={styles.text.subtitle}>{opt.subtitle}</span>
                  ) : null}
                </span>

                <span
                  className={classNames(
                    styles.radio.base,
                    checked ? styles.radio.selected : styles.radio.idle,
                  )}
                >
                  {checked && <span className={styles.radio.dot} />}
                </span>
              </>
            )}
          </Radio>
        ))}
      </RadioGroup>
    </div>
  );
}

const styles = {
  field: {
    wrapper: 'space-y-3',
    label: 'text-[15px] font-bold text-mf-off-black-950',
  },
  group: 'space-y-2',
  card: {
    base: classNames(
      'flex items-center gap-3 rounded-2xl border p-3 cursor-pointer transition-colors',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-mf-blue focus-visible:ring-offset-2',
    ),
    selected: 'border-mf-blue-300 bg-mf-blue-50',
    idle: 'border-gray-200 bg-white hover:bg-gray-50',
  },
  icon: {
    wrap: 'flex-none flex items-center justify-center w-10 h-10 rounded-lg',
    wrapSelected: 'bg-white',
    wrapIdle: 'bg-gray-100',
    iconSelected: 'fa-fw text-mf-blue-800',
    iconIdle: 'fa-fw text-gray-500',
  },
  text: {
    wrap: 'flex-1 min-w-0',
    title: 'block font-semibold text-mf-off-black-950',
    subtitle: 'block text-sm text-gray-500',
  },
  radio: {
    base: 'flex-none flex size-5 items-center justify-center rounded-full border-2',
    selected: 'border-mf-blue',
    idle: 'border-gray-300',
    dot: 'size-2.5 rounded-full bg-mf-blue',
  },
};

export default RadioField;
